// One shared polling scheduler for every page (mounted once by PageLayout, used through subscribePoll):
//  - each subscriber runs on its own interval, measured from the END of its previous run (never overlapped);
//  - nothing runs while the tab is hidden; when it becomes visible again every overdue subscriber runs at once;
//  - a subscriber whose call failed (rejected, or resolved to `false`) backs off ×2 per failure up to 60 s and
//    returns to its interval on the next success.
// Pages call their `load()` once on mount themselves; the scheduler only handles the refreshes.
const MAX_DELAY = 60000, TICK = 1000;
const subs = new Set();
let timer = null;

const hidden = () => typeof document !== "undefined" && document.hidden;

function run(s) {
    s.running = true;
    Promise.resolve().then(() => s.fn())
        .then(ok => { s.delay = ok === false ? Math.min(MAX_DELAY, s.delay * 2) : s.interval; },
              () => { s.delay = Math.min(MAX_DELAY, s.delay * 2); })
        .finally(() => { s.running = false; s.next = Date.now() + s.delay; });
}

function tick() {
    if (hidden()) return;
    const now = Date.now();
    for (const s of subs) if (!s.running && s.next <= now) run(s);
}

function arm() { if (!timer && subs.size && !hidden()) timer = setInterval(tick, TICK); }
function disarm() { if (timer) { clearInterval(timer); timer = null; } }

if (typeof document !== "undefined")
    document.addEventListener("visibilitychange", () => { if (document.hidden) disarm(); else { arm(); tick(); } });

// Registers fn to run every intervalMs; returns the unsubscribe function.
export function subscribePoll(fn, intervalMs) {
    const s = { fn, interval: intervalMs, delay: intervalMs, next: Date.now() + intervalMs, running: false };
    subs.add(s); arm();
    return () => { subs.delete(s); if (!subs.size) disarm(); };
}
